import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTenantService } from 'hooks/api/iam/tenant';
import { useAnalystService } from 'hooks/api/agents/analyst';
import { usePurgeService } from 'hooks/api/purge';
import GmModal from 'app/shared/modal/modal';
import { Icon } from 'assets/icons';
import ContextMenu from 'app/shared/datatable/context-menu/context-menu';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { InsightList } from 'app/shared/insight-list';
import { WalletTopUp } from 'app/shared/topup';
import { SubscriptionAssignment } from 'app/member/modules/tenants/components/subscription/assign';
import { RetentionConfiguration } from 'app/member/modules/tenants/components/retention';
import { InvoiceGeneration } from 'app/member/modules/tenants/components/invoice';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/member/layouts/generic';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import WebDataTable from 'app/shared/datatable/web/datatable';
import { Board, BoardCard } from 'app/shared/board';
import { ViewToggle } from '../shared/view-toggle';
import { SubjectTimeline } from '../shared/timeline/timeline';
import { ResourceLabels } from '../shared/labels/labels';
import { useResourceLabelsByKind } from '../shared/labels/useResourceLabelsByKind';
import { TenantIssues } from 'app/member/modules/issues/shared/tenant-issues';
import { AddIssueToTenantDialog } from 'app/member/modules/issues/shared/add-issue-to-tenant';
import { UpdateContactForm } from './contact-form';
import { TENANT_COLUMNS, TENANT_FEATURES } from './constants';
import { table_config } from './helper';
import boardStyles from 'app/shared/board/board.module.css';
import styles from './tenants.module.css';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatContacts = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && value.includes(':')) {
    const [used, limit] = value.split(':');
    return `${used} / ${limit}`;
  }
  return value;
};

const healthColor = (score) => {
  if (score == null) return 'var(--neutral-dark-4, #d3dce6)';
  if (score >= 80) return 'var(--green-primary, #10b981)';
  if (score >= 60) return 'var(--orange-primary, #f59e0b)';
  return 'var(--red-primary, #ef4444)';
};

const HealthBar = ({ score }) => {
  const color = healthColor(score);
  const pct = Math.min(100, Math.max(0, score ?? 0));

  return (
    <div className={styles.healthWrap}>
      <span className={styles.healthScore} style={{ color }}>
        {score ?? '—'}
      </span>
      <div className={styles.healthRight}>
        <div className={styles.healthTrack}>
          <div className={styles.healthFill} style={{ width: `${pct}%`, background: color }} />
        </div>
        <span className={styles.healthLabel}>health score</span>
      </div>
    </div>
  );
};

const Detail = ({ label, value, wide }) => (
  <div className={`${styles.detail} ${wide ? styles.detailWide : ''}`}>
    <span className={styles.detailLabel}>{label}</span>
    {value || value === 0 ? (
      <span className={styles.detailValue}>{value}</span>
    ) : (
      <span className={styles.detailEmpty}>—</span>
    )}
  </div>
);

const TenantSnapshotModal = ({ tenant, report, actions, onOpen, onAction }) => {
  const [composer_mode, setComposerMode] = useState(null);

  const metadata = tenant?.metadata || {};
  const subscriptions = metadata?.active_subscriptions || [];

  const primary_contact_email =
    tenant?.contact?.email || tenant?.emails?.[0] || tenant?.email || '';
  const primary_contact_phone = tenant?.contact?.phone_number || tenant?.phone_number || '';
  const contacts =
    primary_contact_email || primary_contact_phone
      ? [
          {
            id: tenant?.id,
            first_name: tenant?.contact?.firstname || '',
            last_name: tenant?.contact?.lastname || '',
            email: primary_contact_email,
            phone: primary_contact_phone
          }
        ]
      : [];

  const adopted = TENANT_FEATURES.filter((feature) => metadata[feature.key]);
  const not_adopted = TENANT_FEATURES.filter((feature) => !metadata[feature.key]);

  const details = [
    { label: 'ID', value: tenant?.id },
    { label: 'Subscription', value: subscriptions.length ? subscriptions.join(', ') : 'None' },
    { label: 'Email', value: tenant?.emails?.[0] || tenant?.email, wide: true },
    { label: 'Phone', value: tenant?.phone_number },
    { label: 'Website', value: tenant?.website },
    { label: 'Contacts', value: formatContacts(tenant?.subscriber_count) ?? report?.contact_count },
    { label: 'Date Joined', value: formatDate(tenant?.created_on) },
    { label: 'Last Login', value: formatDateTime(tenant?.last_login || report?.last_login) },
    { label: 'Last Activity', value: formatDateTime(metadata?.last_activity) }
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.left}>
        <section className={styles.overviewCard}>
          <header className={styles.hero}>
            <div className={styles.heroIdentity}>
              <h1 className={styles.name}>{tenant?.name || '—'}</h1>
            </div>
            <div className={styles.heroActions}>
              <span className={styles.iconButton} title="Open tenant">
                <Icon name="open_in_tab" width={18} height={18} onClick={onOpen} />
              </span>
              <span className={styles.iconButton} title="Options">
                <ContextMenu
                  actions={actions}
                  callback={onAction}
                  text={<Icon name="more_vert" width={18} height={18} />}
                />
              </span>
            </div>
          </header>

          <div className={styles.labelsSpacer}>
            <ResourceLabels
              resource={{ kind: 'tenant', id: tenant?.id }}
              show_picker={composer_mode === 'label'}
              onClosePicker={() => setComposerMode(null)}
            />
          </div>

          <HealthBar score={metadata?.health_score} />
        </section>

        <section className={styles.featuresCard}>
          <h2 className={styles.sectionTitle}>Basic Information</h2>
          <div className={styles.overviewSnapshot}>
            <div className={styles.snapshotGrid}>
              {details?.map((detail) => (
                <Detail
                  key={detail.label}
                  label={detail.label}
                  value={detail.value}
                  wide={detail.wide}
                />
              ))}
            </div>
          </div>
        </section>

        {metadata?.at_risk_reason?.length > 0 && (
          <section className={styles.featuresCard}>
            <h2 className={styles.sectionTitle}>At Risk Reasons</h2>
            <InsightList insights={metadata?.at_risk_reason} />
          </section>
        )}

        <section className={styles.featuresCard}>
          <h2 className={styles.sectionTitle}>Feature Adoption</h2>
          <div className={styles.chips}>
            {adopted?.map((feature) => (
              <span key={feature.key} className={`${styles.chip} ${styles.chipAdopted}`}>
                {feature.label}
              </span>
            ))}
            {not_adopted.map((feature) => (
              <span key={feature.key} className={`${styles.chip} ${styles.chipPending}`}>
                {feature.label}
              </span>
            ))}
          </div>
        </section>

        <section className={styles.featuresCard}>
          <TenantIssues tenant={tenant} />
        </section>
      </div>

      <aside className={styles.right}>
        <div className={styles.activityHeader}>
          <h2 className={styles.sectionTitle}>Activity</h2>
          <ContextMenu
            text={
              <span className={styles.addActivityBtn}>
                <Icon name="add" width={16} height={16} />
              </span>
            }
            callback={(fn) => fn()}
            actions={[
              [
                { label: 'Log activity', action: () => setComposerMode('note') },
                { label: 'Schedule call', action: () => setComposerMode('meeting') },
                { label: 'Send message', action: () => setComposerMode('message') },
                { label: 'Add label', action: () => setComposerMode('label') }
              ]
            ]}
          />
        </div>
        <SubjectTimeline
          subject={{ kind: 'tenant', id: tenant?.id, name: tenant?.name }}
          defaultInvitee={{
            id: tenant?.id,
            firstname: tenant?.contact?.firstname || '',
            lastname: tenant?.contact?.lastname || '',
            email: primary_contact_email,
            phone: primary_contact_phone
          }}
          contacts={contacts}
          composer_mode={composer_mode}
          onCloseComposer={() => setComposerMode(null)}
        />
      </aside>
    </div>
  );
};

export const TenantsBoard = () => {
  const navigateTo = useNavigate();
  const { fetchTenants, searchTenants, updateTenant } = useTenantService();
  const { fetchTenantReport } = useAnalystService();
  const { purgeAccount } = usePurgeService();
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const { labels_by_resource } = useResourceLabelsByKind('tenant');

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active_view, setActiveView] = useState('board');
  const [is_search_mode, setSearchMode] = useState(false);
  const [tenant_count, setTenantCount] = useState(0);
  const [selected_tenant, setSelectedTenant] = useState(null);
  const [report, setReport] = useState(null);

  const [action_tenant, setActionTenant] = useState({});
  const [show_topup_modal, setShowTopUpModal] = useState(false);
  const [show_assignment_modal, setShowAssignmentModal] = useState(false);
  const [show_retention_modal, setShowRetentionModal] = useState(false);
  const [show_invoice_modal, setShowInvoiceModal] = useState(false);
  const [show_delete_modal, setShowDeleteModal] = useState(false);
  const [show_issue_modal, setShowIssueModal] = useState(false);
  const [show_contact_modal, setShowContactModal] = useState(false);
  const [show_mark_internal_modal, setShowMarkInternalModal] = useState(false);

  useEffect(() => {
    if (active_view === 'board') handleDataRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active_view]);

  const handleDataRequest = async () => {
    try {
      setLoading(true);
      const { tenants } = await fetchTenants({ query_string: 'population=200' });
      setTenants(
        (tenants || []).map((tenant) => ({ ...tenant, status: tenant?.metadata?.status }))
      );
    } finally {
      setLoading(false);
    }
  };

  const normalizeTenant = (tenant) => ({
    ...tenant,
    status: tenant?.metadata?.status,
    email: tenant?.emails?.[0] || tenant?.email
  });

  const handleListDataRequest = async (
    page,
    population = 10,
    options = { sort_query: '', reset: false }
  ) => {
    try {
      setLoading(true);
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const { tenants: fetched_tenants, size } = await fetchTenants({
        query_string: `${sort_query}&page=${page}&population=${population}`
      });
      setTenantCount(size);
      const normalized = (fetched_tenants || []).map(normalizeTenant);

      setTenants(normalized);
    } finally {
      setLoading(false);
    }
  };

  const handleListSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { tenants: fetched_tenants, size } = await searchTenants(keys, keyword, {
        query_string: `page=${page}&population=${population}`
      });
      setTenantCount(size);
      const normalized = (fetched_tenants || []).map(normalizeTenant);

      setTenants(normalized);
    } finally {
      setLoading(false);
    }
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;
    if (type !== 'single') return;
    if (name === 'view') return handleTenantClick(data);
    handleAction(data, name);
  };

  const handleTenantClick = async (tenant) => {
    setSelectedTenant(tenant);
    setReport(null);
    const { report } = await fetchTenantReport(tenant?.id);
    setReport(report);
  };

  const handleAction = (tenant, value) => {
    setActionTenant(tenant);

    switch (value) {
      case 'view':
        navigateTo(`/tenants/${tenant?.id}/dashboard`);
        break;
      case 'edit':
        navigateTo(`/tenants/${tenant?.id}/edit`);
        break;
      case 'top up':
        setShowTopUpModal(true);
        break;
      case 'assign_subscription':
        setShowAssignmentModal(true);
        break;
      case 'retention_config':
        setShowRetentionModal(true);
        break;
      case 'generate_invoice':
        setShowInvoiceModal(true);
        break;
      case 'add_issue':
        setShowIssueModal(true);
        break;
      case 'update_contact':
        setShowContactModal(true);
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      case 'mark_internal':
        setShowMarkInternalModal(true);
        break;
      default:
        break;
    }
  };

  const handleSingleDeletion = async (permitted) => {
    if (permitted && action_tenant?.id) {
      const result = await purgeAccount(action_tenant?.id);
      if (result) {
        setTenants((prev) => prev.filter((tenant) => tenant?.id !== action_tenant?.id));
      }
    }

    setShowDeleteModal(false);
  };

  const handleMarkAsInternal = async (permitted) => {
    if (permitted && action_tenant?.id) {
      const metadata = { ...action_tenant?.metadata, is_internal: true };
      const result = await updateTenant(action_tenant?.id, { data: { metadata } });

      if (result) {
        setTenants((prev) =>
          prev.map((tenant) =>
            tenant?.id === action_tenant?.id ? { ...tenant, metadata } : tenant
          )
        );
      }
    }

    setShowMarkInternalModal(false);
  };

  const list_items = [...tenants].sort(
    (a, b) => new Date(b.created_on || 0) - new Date(a.created_on || 0)
  );

  const renderCard = (tenant) => (
    <BoardCard
      key={tenant?.id}
      title={tenant?.name || '—'}
      subtitle={tenant?.emails?.[0] || tenant?.email}
      meta={formatDate(tenant?.created_on)}
      labels={labels_by_resource[tenant?.id] || []}
      onClick={() => handleTenantClick(tenant)}
      actions={
        <ContextMenu
          actions={table_config.actions.single(tenant)}
          callback={(value) => handleAction(tenant, value)}
          text={<span className={boardStyles.menuTrigger}>⋯</span>}
        />
      }
    />
  );

  return (
    <>
      <GridRow num_of_columns={12}>
        <GridColumn span={3}>
          <ViewToggle active_view={active_view} onChange={setActiveView} />
        </GridColumn>
        <GridColumn span={9} />
      </GridRow>

      <Spacer multiple={2} />

      {active_view === 'board' && (
        <Board
          columns={TENANT_COLUMNS}
          items={tenants}
          group_by="status"
          loading={loading}
          empty_text="No tenants"
          renderCard={renderCard}
        />
      )}

      {active_view === 'list' &&
        (is_mobile_view ? (
          <MobileDatatable
            config={{
              ...table_config,
              is_search_mode,
              items: list_items,
              total_count: tenant_count
            }}
            action={handleDatatableAction}
            onClick={handleTenantClick}
            onListModeChange={setSearchMode}
            onDataRequest={handleListDataRequest}
            onSearchRequest={handleListSearchRequest}
          />
        ) : (
          <WebDataTable
            config={{
              ...table_config,
              is_search_mode,
              items: list_items,
              total_count: tenant_count
            }}
            action={handleDatatableAction}
            onClick={handleTenantClick}
            loading_data={loading}
            onDataRequest={handleListDataRequest}
            onSearchRequest={handleListSearchRequest}
          />
        ))}

      <GmModal
        show_header={false}
        show_modal={!!selected_tenant}
        bodyClassName={styles.viewModal}
        onClose={() => setSelectedTenant(null)}
      >
        {selected_tenant && (
          <TenantSnapshotModal
            tenant={selected_tenant}
            report={report}
            actions={table_config.actions.single(selected_tenant)}
            onOpen={() => navigateTo(`/tenants/${selected_tenant?.id}/dashboard`)}
            onAction={(value) => {
              const tenant = selected_tenant;
              setSelectedTenant(null);
              handleAction(tenant, value);
            }}
          />
        )}
      </GmModal>

      <ConfirmationDialog
        title="Delete Tenant"
        message="Are you sure you want to delete this tenant?"
        callback={handleSingleDeletion}
        is_open={show_delete_modal}
      />
      <ConfirmationDialog
        title="Mark as Internal"
        message="Are you sure you want to mark this tenant as internal?"
        callback={handleMarkAsInternal}
        is_open={show_mark_internal_modal}
      />
      <GmModal
        title="Wallet Top Up"
        show_title
        show_modal={show_topup_modal}
        onClose={() => setShowTopUpModal(false)}
      >
        <WalletTopUp tenant_id={action_tenant?.id} />
      </GmModal>
      <GmModal
        title="Assign Subscription"
        show_title
        show_modal={show_assignment_modal}
        onClose={() => setShowAssignmentModal(false)}
      >
        <SubscriptionAssignment tenant_id={action_tenant?.id} />
      </GmModal>
      <GmModal
        title="Configure Retention"
        show_title
        show_modal={show_retention_modal}
        onClose={() => setShowRetentionModal(false)}
      >
        <RetentionConfiguration tenant_id={action_tenant?.id} />
      </GmModal>
      <GmModal
        title="Generate Invoice"
        show_title
        show_modal={show_invoice_modal}
        onClose={() => setShowInvoiceModal(false)}
      >
        <InvoiceGeneration tenant={action_tenant} />
      </GmModal>
      <GmModal
        title="Add issue to tenant"
        show_title
        show_modal={show_issue_modal}
        onClose={() => setShowIssueModal(false)}
      >
        {action_tenant?.id && (
          <AddIssueToTenantDialog
            tenant={action_tenant}
            closeModal={() => setShowIssueModal(false)}
          />
        )}
      </GmModal>
      <GmModal
        title="Update Contact Person"
        show_title
        show_modal={show_contact_modal}
        onClose={() => setShowContactModal(false)}
      >
        {action_tenant?.id && (
          <UpdateContactForm
            tenant={action_tenant}
            onUpdated={(contact) => {
              setActionTenant((prev) => ({ ...prev, contact }));
              setTenants((prev) =>
                prev.map((t) => (t.id === action_tenant?.id ? { ...t, contact } : t))
              );
              setSelectedTenant((prev) =>
                prev && prev.id === action_tenant?.id ? { ...prev, contact } : prev
              );
            }}
            closeModal={() => setShowContactModal(false)}
          />
        )}
      </GmModal>
    </>
  );
};
